import { Button, Form, Input, message, Switch } from "antd";
import { useEffect, useState } from "react";
import ContentTitle from "../../../components/ui/ContentTitle";
import { InfoType } from "./types";
import { useGetSalesProfileListQuery } from "../../../graphql/operations/sales188/getSalesProfileList";
import { useUpdateSalesProfileMutation } from "../../../graphql/operations/sales188/updateSalesProfile";
import { useAddSalesProfileInfoMutation } from "../../../graphql/operations/sales188/addSalesProfile";
import { useDeleteSalesProfileInfoMutation } from "../../../graphql/operations/sales188/deleteSalesProfile";
import "./MemberInfo.scss";
import requiredRule from "../../../utils/formRules/requiredRule";
import Spinner from "../../../components/layout/spinner/Spinner";
import NoPermission from "../../../components/others/NoPermission";
import { Dispatch, SetStateAction } from "react";
import { useReactiveVar } from "@apollo/client";
import { __currentUser__ } from "../../../graphql/policies";
import { useGetUserProgramsByServiceQuery } from "../../../graphql/operations/get-user-program-by-service";
import { renderDecodedId } from "../../../utils/renderDecodedId";
import { renderAntDMessageConfig } from "../../../utils/renderAntDMessageConfig";
import { ServiceTypes } from "../../../types/types";

interface MemberInfoProps {
  setSelectedService: Dispatch<SetStateAction<ServiceTypes>>;
  setSelectSubItem: Dispatch<SetStateAction<string | undefined>>;
}

const MemberInfo = ({
  setSelectedService,
  setSelectSubItem,
}: MemberInfoProps) => {
  const [form] = Form.useForm();
  const [employeeId, setEmployeeId] = useState("");
  const [info, setInfo] = useState<InfoType>();
  const [isEditing, setIsEditing] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const { data, loading, refetch } = useGetSalesProfileListQuery({
    variables: {
      employeeId: employeeId,
    },
    skip: !employeeId,
    fetchPolicy: "no-cache",
  });
  const [updateSalesProfile, { loading: updateLoading }] =
    useUpdateSalesProfileMutation();
  const [addSalesProfile, { loading: addLoading }] =
    useAddSalesProfileInfoMutation();
  const [deleteSalesProfile, { loading: deleteLoading }] =
    useDeleteSalesProfileInfoMutation();

  const currentUser = useReactiveVar(__currentUser__);
  const {
    data: userPrograms,
    loading: userProgramsLoading,
    error: userProgramsError,
  } = useGetUserProgramsByServiceQuery({
    variables: {
      userId: parseInt(renderDecodedId(currentUser?.id)),
      serviceId: 5,
    },
  });

  const resetAll = () => {
    form.resetFields();
    setIsEditing(false);
    setInfo(undefined);
    setEmployeeId("");
  };

  const handleSubmit = () => {
    setInfo(undefined);
    const { memberId } = form.getFieldsValue();
    if (memberId !== employeeId) {
      setEmployeeId(memberId);
    } else {
      refetch();
    }
    setIsEditing(true);
  };

  const handleUpdate = async () => {
    const { memberId, iTalkExtension, hqlmUrl } = form.getFieldsValue();
    await updateSalesProfile({
      variables: {
        employeeId: memberId,
        iTalkExtension: iTalkExtension,
        hqlmUrl: hqlmUrl,
      },
    });
    window.location.reload();
  };

  const handleDelete = async () => {
    const { memberId } = form.getFieldsValue();
    const data = await deleteSalesProfile({
      variables: {
        employeeId: memberId,
      },
    });
    if (data.data?.deleteSalesProfileInfo.error) {
      message.error(
        renderAntDMessageConfig(data.data.deleteSalesProfileInfo.error)
      );
    } else {
      message.success(renderAntDMessageConfig("Successfully deleted"));
      resetAll();
    }
  };

  const handleCreate = async () => {
    const { memberId, iTalkExtension, hqlmUrl } = form.getFieldsValue();
    const data = await addSalesProfile({
      variables: {
        employeeId: memberId,
        iTalkExtension: iTalkExtension,
        hqlmUrl: hqlmUrl,
      },
    });
    if (data.data?.addSalesProfileInfo.error) {
      message.error(
        renderAntDMessageConfig(data.data.addSalesProfileInfo.error)
      );
    } else {
      message.success(renderAntDMessageConfig("Successfully created"));
      resetAll();
    }
  };

  const handleSwitchChange = () => {
    resetAll();
    setIsAdding(!isAdding);
  };

  useEffect(() => {
    if (data && data.salesProfileList.length > 0) {
      setInfo(data.salesProfileList[0]);
    } else if (data && data.salesProfileList.length === 0) {
      setInfo(undefined);
      message.error(renderAntDMessageConfig("Wrong Employee Number"));
    }
  }, [data]);

  useEffect(() => {
    if (info) {
      form.setFieldsValue({
        iTalkExtension: info.iTalkExtension,
        hqlmUrl: info.hqlmUrl,
      });
    }
  }, [info, form]);

  useEffect(() => {
    setSelectedService(ServiceTypes.SALES_188);
    setSelectSubItem("Member Info");
  }, []);

  if (userProgramsError) {
    message.error(
      renderAntDMessageConfig(
        "Something went wrong. Please contact your system administrator."
      )
    );
  }

  if (userProgramsLoading) {
    return <Spinner />;
  }

  if (
    userPrograms &&
    !userPrograms.wf_adminFirst_afUserProgram?.edges?.find(
      (item) => item.node.afProgram?.programName === "Member Info"
    )
  ) {
    return <NoPermission />;
  }

  return (
    <div id="memberInfoWrapper">
      <ContentTitle title="Sales Info" />
      <div className="content-container">
        <div className="flex items-center mb-4">
          <p className="mr-4">Add Mode</p>
          <Switch
            checkedChildren="Yes"
            unCheckedChildren="No"
            onChange={handleSwitchChange}
          />
        </div>
        <Form form={form}>
          <div className="flex">
            <Form.Item
              label="Sales Code"
              name="memberId"
              rules={[requiredRule]}
            >
              <Input disabled={isEditing} />
            </Form.Item>
            {!isAdding && (
              <Button
                className="ml-5"
                type="primary"
                onClick={() => {
                  form
                    .validateFields()
                    .then(handleSubmit)
                    .catch((e) => console.log(e));
                }}
                loading={loading}
                disabled={isEditing}
              >
                Query
              </Button>
            )}
            {isEditing && (
              <Button
                className="ml-5"
                onClick={() => {
                  resetAll();
                }}
              >
                Reset
              </Button>
            )}
          </div>
          {(info || isAdding) && (
            <div className="flex">
              <Form.Item
                label="iTalk Extension"
                name="iTalkExtension"
                rules={isEditing || isAdding ? [requiredRule] : []}
              >
                <Input />
              </Form.Item>
              <Form.Item
                className="ml-5"
                label="HQLM Url"
                name="hqlmUrl"
                rules={isEditing || isAdding ? [requiredRule] : []}
              >
                <Input />
              </Form.Item>

              <Button
                className="ml-5"
                type="primary"
                onClick={() => {
                  form
                    .validateFields()
                    .then(() => {
                      if (isAdding) {
                        handleCreate();
                      } else {
                        handleUpdate();
                      }
                    })
                    .catch((e) => console.log(e));
                }}
                loading={updateLoading || addLoading}
              >
                {isAdding ? "Create" : "Update"}
              </Button>
              {!isAdding && (
                <Button
                  className="ml-5"
                  danger
                  onClick={handleDelete}
                  loading={deleteLoading}
                >
                  Delete
                </Button>
              )}
            </div>
          )}
        </Form>
      </div>
    </div>
  );
};

export default MemberInfo;
